import { subtitlesFormats } from '@iconik-io/shared-lib/src/utils/constants/files';
import {
  MEDIA_TYPE_AUDIO,
  MEDIA_TYPE_VIDEO,
} from '@iconik-io/shared-lib/src/utils/media_type';

import ContextMenu from '../../components/context_menu/context_menu';
import contextMenuConnector from '../../components/context_menu/context_menu_connector';
import type {
  ContextMenuContainerProps,
  ContextMenuItemDefinition,
  GetDefinition,
} from '../../components/context_menu/types';
import {
  CONFIRM_PROXY,
  FORMATS_PANEL_NEW_FORMAT,
  NEW_SUBTITLE_MODAL,
} from '../../constants/modals';
import {
  CONTEXT_MENU_PANELS_FILES_TYPE,
  connectContextMenuConsumer,
} from '../../contexts/context_menu';
import { segmentTrackEvent } from '../../ducks/analytics_tracking/thunks';
import { openModal } from '../../ducks/modals_manager/slice';
import {
  selectAssetFormats,
  selectAssetOriginalFormat,
} from '../../ducks/pages/asset/selectors';
import ICONS from '../../icons/constants';
import { getCurrentRouteParams } from '../../router/current_route_params';
import type { AppDispatch, AppState } from '../../store/types';
import { createPickedFile } from '../../utils/create_picked_file';
import { getFormatMediaType } from '../../utils/media_type';

// TODO replace with openFileDialog
//  https://linear.app/iconik/issue/FE-3364/replace-triggering-of-open-file-dialog-with-openfiledialog-from-core
const triggerBrowser = (
  callback: (files: File[], input: HTMLInputElement) => void,
  multiple?: boolean,
  accept?: string,
) => {
  const inputFile: HTMLInputElement = document.createElement('input');
  inputFile.style.position = 'fixed';
  inputFile.style.left = '-100000px';
  inputFile.type = 'file';
  if (multiple) {
    inputFile.multiple = multiple;
  }
  if (accept) {
    inputFile.accept = accept;
  }
  document.body.appendChild(inputFile);
  inputFile.click();
  inputFile.onchange = (): void => {
    if (inputFile.files) {
      callback(Array.from(inputFile.files), inputFile);
    } else {
      callback([], inputFile);
    }
  };
};

const getDefinition: GetDefinition = (
  props: ContextMenuContainerProps,
  state: AppState,
) => {
  const contextItems = props.contextItems;
  const [contextItem] = contextItems;
  const assetId = contextItem?.id;
  const { versionId } = getCurrentRouteParams();
  const asset = assetId ? state.entities.assets[assetId] : undefined;
  const originalFormat = selectAssetOriginalFormat(state, assetId, versionId);
  const originalMediaType = getFormatMediaType(originalFormat);
  const assetFormats = selectAssetFormats(state, assetId, versionId);

  const items: ContextMenuItemDefinition[] = [];
  if (!asset) {
    return { items };
  }
  if (
    originalMediaType === MEDIA_TYPE_VIDEO ||
    originalMediaType === MEDIA_TYPE_AUDIO
  ) {
    items.push({
      label: 'Subtitles',
      icon: ICONS.Subtitle,
      disabled: assetFormats.length === 0,
      action: () => (dispatch: AppDispatch) => {
        triggerBrowser(
          (files) => {
            const pickedFiles = files.map((file) => createPickedFile(file));
            dispatch(
              openModal({
                id: NEW_SUBTITLE_MODAL,
                contextItems: [contextItem],
                options: { files: pickedFiles },
              }),
            );
          },
          true,
          subtitlesFormats.map((f) => `.${f}`).join(),
        );
      },
    });
  }
  items.push(
    {
      label: 'Format',
      icon: ICONS.Transcoder,
      action: () => (dispatch: AppDispatch) => {
        triggerBrowser((files) => {
          if (files.length) {
            dispatch(
              segmentTrackEvent({
                eventName: 'Upload File to Asset',
                properties: {
                  Type: 'Format',
                  Media: originalMediaType,
                },
              }),
            );
            dispatch(
              openModal({
                id: FORMATS_PANEL_NEW_FORMAT,
                contextItems: [asset],
                options: { file: createPickedFile(files[0]) },
              }),
            );
          }
        }, false);
      },
    },
    {
      label: 'Proxy',
      icon: ICONS.Proxy,
      disabled: assetFormats.length === 0,
      action: () => (dispatch: AppDispatch) => {
        triggerBrowser((files) => {
          if (files.length) {
            dispatch(
              segmentTrackEvent({
                eventName: 'Upload File to Asset',
                properties: {
                  Type: 'Proxy',
                  Media: originalMediaType,
                },
              }),
            );
            dispatch(
              openModal({
                id: CONFIRM_PROXY,
                contextItems: [asset],
                options: { file: createPickedFile(files[0]) },
              }),
            );
          }
        }, false);
      },
    },
  );

  return {
    items,
  };
};

const UploadContextMenu = connectContextMenuConsumer(
  contextMenuConnector(getDefinition)(ContextMenu),
  CONTEXT_MENU_PANELS_FILES_TYPE,
);

export default UploadContextMenu;
